import type { LanguageCode } from "../languages/language-codes";
import type { MetadataTitleAccordionItemFragment } from "../client/generated";
import { v4 as uuid } from "uuid";
import { useTranslation } from "react-i18next";
import { AccessButton } from "@acdc2/ui/components/access-button";
import { AccessSeparator } from "@acdc2/ui/components/access-separator";
import { useState } from "react";
import { MetadataTitleInput } from "./MetadataTitleInput";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@acdc2/ui/components/accordion";
import {
  useAddTitleMutation,
  useRemoveTitleMutation,
  useUpdateTitleMutation,
} from "../client/generated";

type Props = { fragment: MetadataTitleAccordionItemFragment };
type UnsavedTitle = { id: string; language?: LanguageCode; title?: string };

export function MetadataTitleAccordionItem({ fragment }: Props) {
  const { t } = useTranslation();
  const [unsavedTitles, setUnsavedTitles] = useState<UnsavedTitle[]>([]);

  const [addTitle] = useAddTitleMutation({ refetchQueries: "active" });
  const [updateTitle] = useUpdateTitleMutation();
  const [removeTitle] = useRemoveTitleMutation({
    update(cache) {
      const id = cache.identify(fragment);
      cache.evict({ id });
      cache.gc();
    },
  });

  const createUnsavedTitle = () => {
    setUnsavedTitles((oldTitles) => [...oldTitles, { id: uuid() }]);
  };

  const removeUnsavedTitle = (titleId: string) => {
    setUnsavedTitles((oldTitles) =>
      oldTitles.filter((title) => title.id !== titleId),
    );
  };

  const createSavedTitle = async (
    titleId: string,
    newLanguage: string,
    newTitle: string,
  ) => {
    const result = await addTitle({
      variables: {
        documentId: fragment.id,
        language: newLanguage,
        title: newTitle,
      },
    });
    if (result.data?.addTitle.title) {
      removeUnsavedTitle(titleId);
    }
  };

  const updateSavedTitle = async (
    titleId: string,
    newLanguage: string,
    newTitle: string,
  ) => {
    await updateTitle({
      variables: {
        titleId,
        language: newLanguage,
        title: newTitle,
      },
    });
  };

  const removeSavedTitle = async (titleId: string) => {
    const result = await removeTitle({ variables: { titleId } });
    if (result.data?.removeTitle.title) {
      setUnsavedTitles((oldTitles) =>
        oldTitles.filter((title) => title.id !== titleId),
      );
    }
  };

  return (
    <AccordionItem value="titles">
      <AccordionTrigger className="px-4">
        {t("MetadataTitleAccordionItem.label")}
      </AccordionTrigger>
      <AccordionContent className="px-4 pt-1 flex flex-col gap-2">
        {fragment.metadata.titles.map((title) => (
          <MetadataTitleInput
            key={title.id}
            defaultLanguage={title.language as LanguageCode}
            defaultTitle={title.title}
            onSave={(newLanguage, newTitle) =>
              updateSavedTitle(title.id, newLanguage, newTitle)
            }
            onDelete={() => removeSavedTitle(title.id)}
          />
        ))}

        {unsavedTitles.length > 0 && (
          <AccessSeparator
            className="my-1"
            title={t("MetadataTitleAccordionItem.separator")}
          />
        )}

        {unsavedTitles.map((title) => (
          <MetadataTitleInput
            key={title.id}
            defaultLanguage={title.language as LanguageCode}
            defaultTitle={title.title}
            onSave={(newLanguage, newTitle) =>
              createSavedTitle(title.id, newLanguage, newTitle)
            }
            onDelete={() => removeUnsavedTitle(title.id)}
          />
        ))}
        <AccessButton type="button" onClick={createUnsavedTitle}>
          {t("MetadataTitleAccordionItem.add")}
        </AccessButton>
      </AccordionContent>
    </AccordionItem>
  );
}
